import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import CustomerService from '../services/customerService';
import OrderService from '../services/orderService';
import { Customer, Order } from '../types';
import AuthService from '../services/authService';
import moment from 'moment';
import { formatUTCToLocal } from '../utils/timezoneUtils';

const CustomerDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const currentUser = AuthService.getCurrentUser();
    const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';

    const [customer, setCustomer] = useState<Customer | null>(null);
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadCustomerAndOrders();
    }, [id]);

    const loadCustomerAndOrders = async () => {
        try {
            setLoading(true);
            // Tải thông tin khách hàng
            const customerResponse = await CustomerService.getById(Number(id));
            setCustomer(customerResponse.data);

            // Tải danh sách đơn hàng của khách hàng
            const ordersResponse = await OrderService.getByCustomer(Number(id));
            setOrders(ordersResponse.data);

            setError(null);
        } catch (err) {
            setError('Không thể tải thông tin khách hàng. Vui lòng thử lại sau.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Bạn có chắc chắn muốn xóa khách hàng này? Tất cả đơn hàng liên quan cũng sẽ bị xóa.')) {
            try {
                await CustomerService.remove(Number(id));
                navigate(`${userPrefix}/customers`);
            } catch (err) {
                setError('Không thể xóa khách hàng. Vui lòng thử lại sau.');
                console.error(err);
            }
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <span className="badge" style={{
                    backgroundColor: 'var(--bs-warning)',
                    color: 'var(--bs-white)'
                }}>Đang xử lý</span>;
            case 'delivered':
                return <span className="badge" style={{
                    backgroundColor: 'var(--bs-success)',
                    color: 'var(--bs-white)'
                }}>Đã giao</span>;
            case 'cancelled':
                return <span className="badge" style={{
                    backgroundColor: 'var(--bs-danger)',
                    color: 'var(--bs-white)'
                }}>Đã hủy</span>;
            default:
                return <span className="badge" style={{
                    backgroundColor: 'var(--bs-secondary)',
                    color: 'var(--bs-white)'
                }}>Không xác định</span>;
        }
    };

    if (loading) {
        return (
            <div className="center-screen">
                <div className="spinner-border" role="status" aria-label="loading"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">{error}</div>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate(`${userPrefix}/customers`)}
                >
                    Trở về danh sách
                </button>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="container mt-4">
                <div className="alert alert-warning">Không tìm thấy thông tin khách hàng.</div>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate(`${userPrefix}/customers`)}
                >
                    Trở về danh sách
                </button>
            </div>
        );
    }

    return (
        <div className="content">
            <div className="row">
                <div className="col-md-12">
                    <div className="card mb-4" style={{ borderRadius: '10px' }}>
                        <div className="card-header" style={{
                            backgroundColor: 'var(--bs-primary)',
                            color: 'var(--bs-white)',
                            borderRadius: '10px 10px 0 0',
                            padding: "10px"
                        }}>
                            <i className="fas fa-user me-1"></i>Thông tin cá nhân
                        </div>
                        <div className="card-body">
                            <div className="row mt-3">
                                <div className="col-md-6">
                                    <p><strong>Số điện thoại:</strong> {customer.phoneNumber}</p>
                                    <p><strong>Tên:</strong> {customer.name || 'Chưa có thông tin'}</p>
                                    <p><strong>Địa chỉ:</strong> {customer.address || 'Chưa có thông tin'}</p>
                                </div>
                                <div className="col-md-6">
                                    <p>
                                        <strong>Ngày tạo:</strong>{' '}
                                        {customer.createdAt
                                            ? new Date(customer.createdAt).toLocaleString()
                                            : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="card-footer bg-transparent">
                            <div className="d-flex justify-content-end" style={{ gap: '0.5rem' }}>
                                <Link
                                    to={`${userPrefix}/customers/${id}`}
                                    className="btn"
                                    style={{
                                        backgroundColor: 'var(--bs-warning)',
                                        color: 'var(--bs-white)',
                                        border: '1px solid var(--bs-warning)'
                                    }}
                                >
                                    <i className="fas fa-edit me-1"></i>Sửa
                                </Link>
                                <button
                                    className="btn"
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: 'var(--bs-danger)',
                                        color: 'var(--bs-white)',
                                        border: '1px solid var(--bs-danger)'
                                    }}
                                >
                                    <i className="fas fa-trash me-1"></i>Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

             {/* Desktop Table View */}
             <div className="d-none d-md-block">
                 <div className="row">
                     <div className="col-md-12">
                         <div className="card" style={{ borderRadius: '10px' }}>
                             <div className="card-header d-flex justify-content-between align-items-center" style={{
                                 backgroundColor: 'var(--bs-success)',
                                 color: 'var(--bs-white)',
                                 borderRadius: '10px 10px 0 0',
                                 padding: '10px'
                             }}>
                                 <div>
                                     <i className="fas fa-shopping-cart me-1"></i>Lịch sử đơn hàng
                                 </div>
                                 <Link to={`${userPrefix}/orders/add?customerId=${id}`} className="btn btn-sm" style={{
                                     backgroundColor: 'var(--bs-white)',
                                     color: 'var(--bs-success)',
                                     border: '1px solid var(--bs-white)'
                                 }}>
                                     <i className="fas fa-plus me-1"></i>Thêm đơn hàng
                                 </Link>
                             </div>
                             <div className="card-body" style={{ padding: 'var(--bs-spacer-3)' }}>
                                 {orders.length > 0 ? (
                                     <div className="table-responsive">
                                         <table className="table table-hover">
                                             <thead style={{ color: 'var(--bs-primary)' }}>
                                                 <tr>
                                                     <th>ID</th>
                                                     <th>Sản phẩm</th>
                                                     <th>Số lượng</th>
                                                     <th>Tổng giá trị</th>
                                                     <th>Tiền cọc</th>
                                                     <th>Ngày giao</th>
                                                     <th>Trạng thái</th>
                                                     <th>Thao tác</th>
                                                 </tr>
                                             </thead>
                                             <tbody>
                                                 {orders.map((order) => (
                                                     <tr key={order.id}>
                                                         <td>{order.id}</td>
                                                         <td>{order.product}</td>
                                                         <td>{order.quantity}</td>
                                                         <td>{order.totalValue.toLocaleString()}đ</td>
                                                         <td>{order.deposit.toLocaleString()}đ</td>
                                                         <td>
                                                             {formatUTCToLocal(order.deliveryDate, 'DD/MM/YYYY HH:mm')}
                                                         </td>
                                                         <td>{getStatusBadge(order.status)}</td>
                                                         <td>
                                                             <Link
                                                                 to={`${userPrefix}/orders/${order.id}`}
                                                                 className="btn btn-sm me-1"
                                                                 style={{
                                                                     backgroundColor: 'var(--bs-info)',
                                                                     color: 'var(--bs-white)',
                                                                     border: '1px solid var(--bs-info)'
                                                                 }}
                                                             >
                                                                 <i className="fas fa-eye me-1"></i>Xem
                                                             </Link>
                                                             <Link
                                                                 to={`${userPrefix}/orders/edit/${order.id}`}
                                                                 className="btn btn-sm"
                                                                 style={{
                                                                     backgroundColor: 'var(--bs-warning)',
                                                                     color: 'var(--bs-white)',
                                                                     border: '1px solid var(--bs-warning)'
                                                                 }}
                                                             >
                                                                 <i className="fas fa-edit me-1"></i>Sửa
                                                             </Link>
                                                         </td>
                                                     </tr>
                                                 ))}
                                             </tbody>
                                         </table>
                                     </div>
                                 ) : (
                                     <div className="alert alert-info">
                                         <i className="fas fa-info-circle me-2"></i>
                                         Khách hàng này chưa có đơn hàng nào.
                                         <Link to={`${userPrefix}/orders/add?customerId=${id}`} className="ms-2">
                                             <i className="fas fa-plus me-1"></i>thêm đơn hàng mới
                                         </Link>
                                     </div>
                                 )}
                             </div>
                         </div>
                     </div>
                 </div>
             </div>

             {/* Mobile Card View */}
             <div className="d-md-none">
                 <div className="d-flex justify-content-between align-items-center mb-3">
                     <h5 className="mb-0" style={{ color: 'var(--bs-primary)' }}>
                         <i className="fas fa-shopping-cart me-2"></i>Lịch sử đơn hàng
                     </h5>
                     <Link to={`${userPrefix}/orders/add?customerId=${id}`} className="btn btn-sm" style={{
                         backgroundColor: 'var(--bs-success)',
                         color: 'var(--bs-white)',
                         border: '1px solid var(--bs-success)'
                     }}>
                         <i className="fas fa-plus me-1"></i>Thêm đơn hàng
                     </Link>
                 </div>
                 
                 {orders.length > 0 ? (
                     orders.map((order) => (
                         <div key={order.id} className="card mb-3" style={{ borderRadius: '10px', border: '1px solid var(--bs-gray-300)' }}>
                             <div className="card-header d-flex justify-content-between align-items-center" style={{
                                 backgroundColor: 'var(--bs-white)',
                                 borderRadius: '10px 10px 0 0',
                                 padding: '12px',
                                 borderBottom: '1px solid var(--bs-gray-200)'
                             }}>
                                 <div style={{ fontWeight: '600', color: 'var(--bs-gray-800)' }}>
                                     {formatUTCToLocal(order.deliveryDate, 'DD/MM/YYYY')}
                                 </div>
                                 <div>
                                     {getStatusBadge(order.status)}
                                 </div>
                             </div>
                             <div className="card-body" style={{ padding: '15px' }}>
                                 <div className="mb-2">
                                     <div className="d-flex justify-content-between align-items-start">
                                         <div>
                                             <div style={{ fontWeight: '500', color: 'var(--bs-gray-800)' }}>{order.product}</div>
                                             <div style={{ fontSize: '0.875rem', color: 'var(--bs-gray-600)' }}>
                                                 {order.totalValue.toLocaleString()}đ x {order.quantity} cái
                                             </div>
                                         </div>
                                         <div style={{ fontWeight: '600', color: 'var(--bs-gray-800)' }}>
                                             {(order.totalValue * order.quantity).toLocaleString()}đ
                                         </div>
                                     </div>
                                 </div>
                             </div>
                             <div className="card-footer" style={{
                                 backgroundColor: 'var(--bs-gray-50)',
                                 borderTop: '1px solid var(--bs-gray-200)',
                                 borderRadius: '0 0 10px 10px',
                                 padding: '12px 15px'
                             }}>
                                 <div className="d-flex justify-content-between align-items-center mb-2">
                                     <span style={{ fontSize: '0.875rem', color: 'var(--bs-gray-600)' }}>Đã trả:</span>
                                     <span style={{ fontSize: '0.875rem', color: 'var(--bs-gray-600)' }}>{order.deposit.toLocaleString()}đ</span>
                                 </div>
                                 <div className="d-flex justify-content-between align-items-center mb-2">
                                     <span style={{ fontSize: '0.875rem', color: 'var(--bs-danger)' }}>Còn nợ:</span>
                                     <span style={{ fontSize: '0.875rem', color: 'var(--bs-danger)', fontWeight: '600' }}>
                                         {(order.totalValue * order.quantity - order.deposit).toLocaleString()}đ
                                     </span>
                                 </div>
                                 <div className="d-flex justify-content-between align-items-center">
                                     <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--bs-gray-800)' }}>Tổng tiền:</span>
                                     <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--bs-gray-800)' }}>
                                         {(order.totalValue * order.quantity).toLocaleString()}đ
                                     </span>
                                 </div>
                                 <div className="d-flex justify-content-end mt-3" style={{ gap: '0.5rem' }}>
                                     <Link
                                         to={`${userPrefix}/orders/${order.id}`}
                                         className="btn btn-sm"
                                         style={{
                                             backgroundColor: 'var(--bs-info)',
                                             color: 'var(--bs-white)',
                                             borderTop: '1px solid var(--bs-info)',
                                             fontSize: '0.875rem'
                                         }}
                                     >
                                         <i className="fas fa-eye me-1"></i>Xem
                                     </Link>
                                     <Link
                                         to={`${userPrefix}/orders/edit/${order.id}`}
                                         className="btn btn-sm"
                                         style={{
                                             backgroundColor: 'var(--bs-warning)',
                                             color: 'var(--bs-white)',
                                             border: '1px solid var(--bs-warning)',
                                             fontSize: '0.875rem'
                                         }}
                                     >
                                         <i className="fas fa-edit me-1"></i>Sửa
                                     </Link>
                                 </div>
                             </div>
                         </div>
                     ))
                 ) : (
                     <div className="alert alert-info">
                         <i className="fas fa-info-circle me-2"></i>
                         Khách hàng này chưa có đơn hàng nào.
                         <Link to={`${userPrefix}/orders/add?customerId=${id}`} className="ms-2">
                             <i className="fas fa-plus me-1"></i>thêm đơn hàng mới
                         </Link>
                     </div>
                 )}
             </div>

            {/* Back button removed — handled by MobileHeader */}
        </div>
    );
};

export default CustomerDetail; 